import { useSyncExternalStore } from 'react'
import { onEdgeEvent } from './commandStream'

/**
 * WHERE EACH "INSTALL FOR ME" IS UP TO — the installing machine announces the start, its
 * latest line (at most once a second) and the end as `cli_install_progress` edge events
 * (edge cliInstall.ts). Module-level, like restoreJobs, so the create sheet can close and
 * open again and still show the install that is running.
 *
 * Keyed by machine and CLI. Never backwards: an event from an earlier run (`started_at`)
 * does not overwrite a later one, and a finished run is not un-finished by an event that
 * was overtaken on the way.
 */
export type InstallJob = {
  cli: string
  state: 'running' | 'done' | 'failed'
  started_at: number
  last_line?: string
  tail?: string[]
  error?: string | null
  exit_code?: number | null
  /** The signal that ended the installer, when one did. */
  signal?: string
  /** What came with it, in order, each with how it went (edge installExtras.ts). */
  extras?: InstallExtra[]
}

/** An extra an install brings (llama.cpp, Ollama with Hermes). `command` is the line to run by
 *  hand when it could not be added. */
export type InstallExtra = {
  key: string
  label: string
  state: 'pending' | 'running' | 'done' | 'failed' | 'skipped'
  detail?: string
  command?: string
}

const jobs = new Map<string, InstallJob>()
const listeners = new Set<() => void>()
const doneListeners = new Set<(device: string, job: InstallJob) => void>()
const key = (device: string, cli: string) => `${device}\u0000${cli}`

const installOver = (j: InstallJob | null | undefined) => j?.state === 'done' || j?.state === 'failed'

/** A job as the machine described it — from an event, the POST's answer or the floor's GET. */
export function noteInstall(device: string, job: unknown): void {
  const j = job as Partial<InstallJob> | null
  if (!j || typeof j.cli !== 'string' || typeof j.started_at !== 'number') return
  if (j.state !== 'running' && j.state !== 'done' && j.state !== 'failed') return
  const k = key(device, j.cli)
  const prev = jobs.get(k)
  if (prev && prev.started_at > j.started_at) return
  if (prev && prev.started_at === j.started_at && installOver(prev) && !installOver(j as InstallJob)) return
  if (prev && JSON.stringify(prev) === JSON.stringify(j)) return
  jobs.set(k, j as InstallJob)
  for (const l of [...listeners]) l()
  // A run that has just become DONE is news for the type list, whichever type is showing.
  if (j.state === 'done' && !(prev && prev.started_at === j.started_at && prev.state === 'done')) {
    for (const l of [...doneListeners]) l(device, j as InstallJob)
  }
}

/** Called once per run that ends with the CLI installed. Returns an unsubscribe. */
export function onInstallDone(fn: (device: string, job: InstallJob) => void): () => void {
  doneListeners.add(fn)
  return () => doneListeners.delete(fn)
}

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

export function useInstallJob(device: string, cli: string): InstallJob | null {
  return useSyncExternalStore(subscribe, () => jobs.get(key(device, cli)) ?? null)
}

onEdgeEvent((event, data) => {
  if (event !== 'cli_install_progress') return
  const device = typeof data['device_id'] === 'string' ? data['device_id'] : ''
  if (device) noteInstall(device, data['job'])
})

/** Tests only. */
export function __resetInstallJobs(): void {
  jobs.clear()
}
